import { useState, useEffect } from 'react';
import { Menu, X, Phone, Mail } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';

export const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Close mobile menu on route change
    setIsOpen(false);
  }, [location]);

  const isActive = (path) => location.pathname === path;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-card/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
      }`}
    >
      {/* Top bar with contact info */}
      <div className={`border-b border-border/50 transition-all duration-300 ${
        isScrolled ? 'h-0 overflow-hidden opacity-0' : 'h-auto opacity-100'
      }`}>
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between py-2 text-sm">
            <div className="flex items-center gap-6">
              <a href="tel:(647)-642-0062" className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors">
                <Phone className="h-4 w-4" />
                <span>(647)-642-0062</span>
              </a>
              <a href="mailto:info@synclinelogistics.com" className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors">
                <Mail className="h-4 w-4" />
                <span>info@synclinelogistics.com</span>
              </a>
            </div>
            <div className="text-muted-foreground">
              Serving GTA & Ontario | 24/7 Support
            </div>
          </div>
        </div>
      </div>

      {/* Main navigation */}
      <nav className="container mx-auto px-4">
        <div className="flex items-center justify-between py-4">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_79ae8060-e1f8-4d28-b152-a3397f858fee/artifacts/8r1orql6_syncline%20logistics%20logo.png" 
              alt="Syncline Logistics" 
              className="h-12 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            <Link 
              to="/services" 
              className={`transition-colors font-medium ${
                isActive('/services') ? 'text-primary' : 'text-foreground hover:text-primary'
              }`}
            >
              Services
            </Link>
            <Link 
              to="/coverage" 
              className={`transition-colors font-medium ${
                isActive('/coverage') ? 'text-primary' : 'text-foreground hover:text-primary'
              }`}
            >
              Coverage
            </Link>
            <Link 
              to="/about" 
              className={`transition-colors font-medium ${
                isActive('/about') ? 'text-primary' : 'text-foreground hover:text-primary'
              }`}
            >
              About
            </Link>
            <Link 
              to="/track" 
              className={`transition-colors font-medium ${
                isActive('/track') ? 'text-primary' : 'text-foreground hover:text-primary'
              }`}
            >
              Track Shipment
            </Link>
            <Link 
              to="/testimonials" 
              className={`transition-colors font-medium ${
                isActive('/testimonials') ? 'text-primary' : 'text-foreground hover:text-primary'
              }`}
            >
              Testimonials
            </Link>
          </div>

          {/* CTA Button */}
          <div className="hidden lg:block">
            <Link to="/quote">
              <Button size="lg" className="bg-gradient-primary hover:shadow-glow transition-all duration-300">
                Get a Quote
              </Button>
            </Link>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden text-foreground hover:text-primary transition-colors"
          >
            {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="lg:hidden pb-4 animate-slide-up">
            <div className="flex flex-col gap-4">
              <Link to="/services" className="text-left text-foreground hover:text-primary transition-colors font-medium">
                Services
              </Link>
              <Link to="/coverage" className="text-left text-foreground hover:text-primary transition-colors font-medium">
                Coverage
              </Link>
              <Link to="/about" className="text-left text-foreground hover:text-primary transition-colors font-medium">
                About
              </Link>
              <Link to="/track" className="text-left text-foreground hover:text-primary transition-colors font-medium">
                Track Shipment
              </Link>
              <Link to="/testimonials" className="text-left text-foreground hover:text-primary transition-colors font-medium">
                Testimonials
              </Link>
              <Link to="/quote">
                <Button size="lg" className="bg-gradient-primary w-full mt-2">
                  Get a Quote
                </Button>
              </Link>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Navigation;
